<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between gap-4">
            <div>
                <h1 class="text-2xl font-semibold">
                    Lite Integrations
                </h1>
                <p class="text-sm text-gray-500">
                    Create and manage Lite Integrations.
                </p>
            </div>
            <button
                type="button"
                class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                @click="openCreateForm"
            >
                New Lite Integration
            </button>
        </div>
        <div
            v-if="error"
            class="rounded-md bg-red-50 p-4 text-sm text-red-700"
        >
            {{ error }}
        </div>
        <div
            v-if="importSuccess"
            class="rounded-md bg-green-50 p-4 text-sm text-green-700"
        >
            {{ importSuccess }}
        </div>
        <div
            v-if="loading"
            class="py-10 text-center text-gray-500"
        >
            Loading Lite Integrations...
        </div>
        <div
            v-else-if="!liteIntegrations.length"
            class="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500"
        >
            No Lite Integrations have been created yet.
        </div>
        <div
            v-else
            class="overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Name
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Start
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                End
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                13+
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Test Mode
                            </th>
                            <th class="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                Version
                            </th>

                            <th class="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                        <tr
                            v-for="liteIntegration in liteIntegrations"
                            :key="liteIntegration.uuid"
                            class="hover:bg-gray-50"
                        >
                            <td class="px-4 py-3">
                                <div class="font-medium text-gray-900">
                                    {{ liteIntegration.name }}
                                </div>
                                <div class="text-xs text-gray-400">
                                    {{ liteIntegration.uuid }}
                                </div>
                            </td>
                            <td class="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                                {{ formatDate(liteIntegration.start_date_time) }}
                            </td>
                            <td class="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                                {{ formatDate(liteIntegration.end_date_time) }}
                            </td>
                            <td class="px-4 py-3 text-sm">
                                {{ liteIntegration.thirteen_plus ? 'Yes' : 'No' }}
                            </td>
                            <td class="px-4 py-3">
                                <span
                                    :class="[
                                        'inline-flex rounded-full px-2 py-1 text-xs font-medium',
                                        liteIntegration.test_mode_bool
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-green-100 text-green-800',
                                    ]"
                                >
                                    {{ liteIntegration.test_mode_bool ? 'Test' : 'Live' }}
                                </span>
                            </td>
                            <td class="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-700">
                                v{{ liteIntegration.version_number ?? 1 }}
                            </td>

                            <td class="whitespace-nowrap px-4 py-3 text-right">
                                <div class="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        class="rounded-md border border-blue-300 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50"
                                        @click="selectedLiteIntegration = liteIntegration"
                                    >
                                        Manage
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md border border-green-300 px-3 py-1.5 text-sm text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        :disabled="exportingUuid === liteIntegration.uuid"
                                        @click="exportLiteIntegration(liteIntegration)"
                                    >
                                        {{
                                            exportingUuid === liteIntegration.uuid
                                                ? 'Exporting...'
                                                : 'Export CSV'
                                        }}
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md border border-purple-300 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        :disabled="importingUuid === liteIntegration.uuid"
                                        @click="selectImportCsv(liteIntegration)"
                                    >
                                        {{
                                            importingUuid === liteIntegration.uuid
                                                ? 'Importing...'
                                                : 'Import CSV'
                                        }}
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md border border-amber-300 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        :disabled="incrementingVersionUuid === liteIntegration.uuid"
                                        @click="incrementVersion(liteIntegration)"
                                    >
                                        {{ incrementingVersionUuid === liteIntegration.uuid ? 'Incrementing...' : 'Increment Version' }}
                                    </button>

                                    <button
                                        type="button"
                                        class="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                                        @click="openEditForm(liteIntegration)"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                                        @click="deleteLiteIntegration(liteIntegration)"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div
                v-if="pagination.lastPage > 1"
                class="flex items-center justify-between border-t border-gray-200 px-4 py-3"
            >
                <button
                    type="button"
                    class="rounded-md border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="pagination.currentPage <= 1"
                    @click="loadLiteIntegrations(pagination.currentPage - 1)"
                >
                    Previous
                </button>
                <span class="text-sm text-gray-500">
                    Page {{ pagination.currentPage }} of {{ pagination.lastPage }}
                </span>
                <button
                    type="button"
                    class="rounded-md border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="pagination.currentPage >= pagination.lastPage"
                    @click="loadLiteIntegrations(pagination.currentPage + 1)"
                >
                    Next
                </button>
            </div>
        </div>
        <div
            v-if="formVisible"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            @click.self="closeForm"
        >
            <div class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
                <div class="flex items-center justify-between border-b px-6 py-4">
                    <h2 class="text-lg font-semibold">
                        {{ editingLiteIntegration ? 'Edit Lite Integration' : 'New Lite Integration' }}
                    </h2>
                    <button
                        type="button"
                        class="text-2xl leading-none text-gray-400 hover:text-gray-600"
                        @click="closeForm"
                    >
                        &times;
                    </button>
                </div>
                <form
                    class="space-y-5 p-6"
                    @submit.prevent="saveLiteIntegration"
                >
                    <div>
                        <label class="mb-1 block text-sm font-medium">
                            Name
                        </label>
                        <input
                            v-model="form.name"
                            type="text"
                            class="w-full rounded-md border border-gray-300 px-3 py-2"
                        >
                        <p
                            v-if="validationErrors.name"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ validationErrors.name[0] }}
                        </p>
                    </div>
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label class="mb-1 block text-sm font-medium">
                                Start date/time
                            </label>
                            <input
                                v-model="form.start_date_time"
                                type="datetime-local"
                                class="w-full rounded-md border border-gray-300 px-3 py-2"
                            >
                            <p
                                v-if="validationErrors.start_date_time"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ validationErrors.start_date_time[0] }}
                            </p>
                        </div>
                        <div>
                            <label class="mb-1 block text-sm font-medium">
                                End date/time
                            </label>
                            <input
                                v-model="form.end_date_time"
                                type="datetime-local"
                                class="w-full rounded-md border border-gray-300 px-3 py-2"
                            >
                            <p
                                v-if="validationErrors.end_date_time"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ validationErrors.end_date_time[0] }}
                            </p>
                        </div>
                    </div>
                    <div>
                        <label class="mb-1 block text-sm font-medium">
                            Event name prefix
                        </label>
                        <input
                            v-model="form.event_name_prefix"
                            type="text"
                            class="w-full rounded-md border border-gray-300 px-3 py-2"
                        >
                        <p
                            v-if="validationErrors.event_name_prefix"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ validationErrors.event_name_prefix[0] }}
                        </p>
                    </div>
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label class="flex cursor-pointer items-center gap-3 rounded-md border p-4">
                            <input
                                v-model="form.thirteen_plus"
                                type="checkbox"
                                class="h-4 w-4"
                            >
                            <div>
                                <div class="text-sm font-medium">
                                    13+
                                </div>
                                <div class="text-xs text-gray-500">
                                    Integration is intended for 13+ users.
                                </div>
                            </div>
                        </label>
                        <label class="flex cursor-pointer items-center gap-3 rounded-md border p-4">
                            <input
                                v-model="form.test_mode_bool"
                                type="checkbox"
                                class="h-4 w-4"
                            >
                            <div>
                                <div class="text-sm font-medium">
                                    Test Mode
                                </div>
                                <div class="text-xs text-gray-500">
                                    Run this integration in test mode.
                                </div>
                            </div>
                        </label>
                    </div>
                    <div>
                        <label class="mb-1 block text-sm font-medium">
                            Test users CSV
                        </label>
                        <textarea
                            v-model="form.test_user_csv"
                            rows="6"
                            class="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                            placeholder="user1,user2,user3"
                        />
                        <p
                            v-if="validationErrors.test_user_csv"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ validationErrors.test_user_csv[0] }}
                        </p>
                    </div>
                    <div
                        v-if="formError"
                        class="rounded-md bg-red-50 p-3 text-sm text-red-700"
                    >
                        {{ formError }}
                    </div>
                    <div class="flex justify-end gap-3 border-t pt-5">
                        <button
                            type="button"
                            class="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                            :disabled="saving"
                            @click="closeForm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            :disabled="saving"
                        >
                            {{ saving ? 'Saving...' : 'Save Lite Integration' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div
            v-if="selectedLiteIntegration"
            class="rounded-lg border border-gray-200 bg-gray-50 p-5"
        >
            <div class="mb-5 flex items-center justify-between">
                <div>
                    <h2 class="text-xl font-semibold">
                        {{ selectedLiteIntegration.name }}
                    </h2>
                    <div class="text-sm text-gray-500">
                        Manage integration entities · Version {{ selectedLiteIntegration.version_number ?? 1 }}
                    </div>
                </div>
                <button
                    type="button"
                    class="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-white"
                    @click="selectedLiteIntegration = null"
                >
                    Close
                </button>
            </div>
            <div class="mb-6 rounded-md border border-red-200 bg-blue-50 p-4">
                <div class="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
                    <div class="flex items-center justify-between gap-4">
                        <div>
                            <div class="text-sm font-medium text-blue-800">
                                Apply Assets to All Locations
                            </div>
                            <div class="mt-1 text-sm text-blue-600">
                                Apply every experience asset to every asset location
                                in this Lite Integration.
                            </div>
                        </div>
                        <button
                            type="button"
                            class="shrink-0 rounded-md border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="applyingAssetsToLocations"
                            @click="applyAssetsToAllLocations"
                        >
                            {{
                                applyingAssetsToLocations
                                    ? 'Applying...'
                                    : 'Apply to All Locations'
                            }}
                        </button>
                    </div>
                </div>
                <div class="flex items-center justify-between gap-4">
                    <div>
                        <div class="text-sm font-medium text-red-800">
                            Reset All Roblox IDs
                        </div>
                        <div class="mt-1 text-sm text-red-600">
                            Reset all integration, asset and placement IDs to zeroes.
                        </div>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="resettingRobloxIds"
                        @click="resetRobloxIds"
                    >
                        {{ resettingRobloxIds ? 'Resetting...' : 'Reset All Roblox IDs' }}
                    </button>
                </div>
            </div>
            <LiteIntegrationAssets
                :key="`assets-${selectedLiteIntegration.uuid}`"
                :lite-integration="selectedLiteIntegration"
                @assets-changed="assetsRevision++"
                @experience-assets-changed="experienceAssetsRevision++"
            />
            <LiteIntegrationExperiences
                :key="selectedLiteIntegration.uuid"
                :lite-integration="selectedLiteIntegration"
                :assets-revision="assetsRevision"
                :experience-assets-revision="experienceAssetsRevision"
                @experience-assets-changed="experienceAssetsRevision++"
            />
            <LiteIntegrationAssetLocations
                :key="`asset-locations-${selectedLiteIntegration.uuid}`"
                :lite-integration="selectedLiteIntegration"
                :experience-assets-revision="experienceAssetsRevision"
            />
            <LiteIntegrationCountries
                :key="`countries-${selectedLiteIntegration.uuid}`"
                :lite-integration="selectedLiteIntegration"
                @integration-updated="handleLiteIntegrationUpdated"
            />
        </div>
        <input
            ref="importCsvInput"
            type="file"
            accept=".csv,text/csv"
            class="hidden"
            @change="importLiteIntegration"
        />
    </div>
</template>
<script>
import LiteIntegrationExperiences from '@/components/LiteIntegrationExperiences.vue';
import LiteIntegrationAssets from '@/components/LiteIntegrationAssets.vue';
import LiteIntegrationAssetLocations from '@/components/LiteIntegrationAssetLocations.vue';
import LiteIntegrationCountries from '@/components/LiteIntegrationCountries.vue';
const emptyForm = () => ({
    name: '',
    start_date_time: '',
    end_date_time: '',
    thirteen_plus: false,
    event_name_prefix: '',
    test_mode_bool: false,
    test_user_csv: '',
});
export default {
    name: 'LiteIntegrationManager',
    components: {
        LiteIntegrationExperiences,
        LiteIntegrationAssets,
        LiteIntegrationAssetLocations,
        LiteIntegrationCountries,
    },
    data() {
        return {
            selectedLiteIntegration: null,
            liteIntegrations: [],
            loading: false,
            saving: false,
            exportingUuid: null,
            formVisible: false,
            editingLiteIntegration: null,
            error: null,
            formError: null,
            validationErrors: {},
            assetsRevision: 0,
            experienceAssetsRevision: 0,
            resettingRobloxIds: false,
            applyingAssetsToLocations: false,
            incrementingVersionUuid: null,
            importingUuid: null,
            importingLiteIntegration: null,
            importSuccess: null,
            form: emptyForm(),
            pagination: {
                currentPage: 1,
                lastPage: 1,
            },
        };
    },
    mounted() {
        this.loadLiteIntegrations();
    },
    methods: {
        async loadLiteIntegrations(page = 1) {
            this.loading = true;
            this.error = null;
            try {
                const response = await this.$http.get(
                    '/lite-integrations',
                    {
                        params: { page },
                    }
                );
                this.liteIntegrations = response.data.data ?? [];
                this.pagination = {
                    currentPage: response.data.meta?.current_page ?? 1,
                    lastPage: response.data.meta?.last_page ?? 1,
                };
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load Lite Integrations.'
                );
            } finally {
                this.loading = false;
            }
        },
        openCreateForm() {
            this.editingLiteIntegration = null;
            this.form = emptyForm();
            this.resetFormErrors();
            this.formVisible = true;
        },
        openEditForm(liteIntegration) {
            this.editingLiteIntegration = liteIntegration;
            this.resetFormErrors();
            this.form = {
                name: liteIntegration.name ?? '',
                start_date_time: this.toDateTimeLocal(
                    liteIntegration.start_date_time
                ),
                end_date_time: this.toDateTimeLocal(
                    liteIntegration.end_date_time
                ),
                thirteen_plus: Boolean(liteIntegration.thirteen_plus),
                event_name_prefix: liteIntegration.event_name_prefix ?? '',
                test_mode_bool: Boolean(liteIntegration.test_mode_bool),
                test_user_csv: liteIntegration.test_user_csv ?? '',
            };
            this.formVisible = true;
        },
        closeForm() {
            if (this.saving) {
                return;
            }
            this.formVisible = false;
            this.editingLiteIntegration = null;
            this.form = emptyForm();
            this.resetFormErrors();
        },
        async saveLiteIntegration() {
            this.saving = true;
            this.resetFormErrors();
            try {
                if (this.editingLiteIntegration) {
                    await this.$http.patch(
                        `/lite-integrations/${this.editingLiteIntegration.uuid}`,
                        this.form
                    );
                } else {
                    await this.$http.post(
                        '/lite-integrations',
                        this.form
                    );
                }
                this.closeForm();
                await this.loadLiteIntegrations(
                    this.pagination.currentPage
                );

                this.refreshSelectedLiteIntegration();
            } catch (error) {
                if (error.response?.status === 422) {
                    this.validationErrors =
                        error.response.data.errors ?? {};
                    this.formError =
                        error.response.data.message ??
                        'Please correct the validation errors.';
                    return;
                }
                this.formError = this.getErrorMessage(
                    error,
                    'Unable to save the Lite Integration.'
                );
            } finally {
                this.saving = false;
            }
        },
        async deleteLiteIntegration(liteIntegration) {
            const confirmed = window.confirm(
                `Delete "${liteIntegration.name}"?`
            );
            if (!confirmed) {
                return;
            }
            this.error = null;
            try {
                await this.$http.delete(
                    `/lite-integrations/${liteIntegration.uuid}`
                );
                const remainingItems =
                    this.liteIntegrations.length - 1;
                const page =
                    remainingItems === 0 &&
                    this.pagination.currentPage > 1
                        ? this.pagination.currentPage - 1
                        : this.pagination.currentPage;
                await this.loadLiteIntegrations(page);
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to delete the Lite Integration.'
                );
            }
        },
        resetFormErrors() {
            this.formError = null;
            this.validationErrors = {};
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }
            return new Date(value).toLocaleString();
        },
        toDateTimeLocal(value) {
            if (!value) {
                return '';
            }
            const date = new Date(value);
            const timezoneOffset =
                date.getTimezoneOffset() * 60000;
            return new Date(date.getTime() - timezoneOffset)
                .toISOString()
                .slice(0, 16);
        },
        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
        async exportLiteIntegration(liteIntegration) {
            if (this.exportingUuid) {
                return;
            }
            this.exportingUuid = liteIntegration.uuid;
            this.error = null;
            try {
                const response = await this.$http.get(
                    `/lite-integrations/${liteIntegration.uuid}/export`,
                    {
                        responseType: 'blob',
                    }
                );
                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: 'text/csv',
                    })
                );
                const link = document.createElement('a');
                link.href = url;
                link.download = this.getExportFilename(
                    response,
                    liteIntegration
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to export the Lite Integration.'
                );
            } finally {
                this.exportingUuid = null;
            }
        },
        getExportFilename(response, liteIntegration) {
            const disposition = response.headers[
                'content-disposition'
            ];
            const filename = disposition
                ?.match(/filename="?([^"]+)"?/i)?.[1];
            if (filename) {
                return filename;
            }
            return `lite-integration-${liteIntegration.uuid}.csv`;
        },
        selectImportCsv(liteIntegration) {
            if (this.importingUuid) {
                return;
            }
            this.importingLiteIntegration = liteIntegration;
            this.importSuccess = null;
            this.error = null;
            this.$refs.importCsvInput.value = '';
            this.$refs.importCsvInput.click();
        },
        async importLiteIntegration(event) {
            const file = event.target.files?.[0];
            const liteIntegration = this.importingLiteIntegration;
            if (!file || !liteIntegration) {
                return;
            }
            this.importingUuid = liteIntegration.uuid;
            this.importSuccess = null;
            this.error = null;
            const formData = new FormData();
            formData.append('csv', file);
            try {
                const response = await this.$http.post(
                    `/lite-integrations/${liteIntegration.uuid}/import`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                this.importSuccess =
                    response.data.message ??
                    'Roblox IDs imported successfully.';
                if (
                    this.selectedLiteIntegration?.uuid ===
                    liteIntegration.uuid
                ) {
                    await this.loadLiteIntegrations(
                        this.pagination.currentPage
                    );
                    this.selectedLiteIntegration =
                        this.liteIntegrations.find(
                            ({ uuid }) => uuid === liteIntegration.uuid
                        ) ?? null;
                    this.experienceAssetsRevision++;
                }
            } catch (error) {
                this.error = this.getImportErrorMessage(error);
            } finally {
                this.importingUuid = null;
                this.importingLiteIntegration = null;
                event.target.value = '';
            }
        },
        getImportErrorMessage(error) {
            const data = error.response?.data;
            if (!data) {
                return 'Unable to import the CSV.';
            }
            if (data.message) {
                return data.message;
            }
            return 'Unable to import the CSV.';
        },
        async resetRobloxIds() {
            if (!this.selectedLiteIntegration || this.resettingRobloxIds) {
                return;
            }
            const confirmed = window.confirm(
                `Reset ALL Roblox IDs for "${this.selectedLiteIntegration.name}"?\n\n` +
                'This will reset every integration ID, asset ID and placement ID ' +
                'associated with this Lite Integration to zeroes.\n\n' +
                'This action cannot be undone.'
            );
            if (!confirmed) {
                return;
            }
            this.resettingRobloxIds = true;
            this.error = null;
            this.importSuccess = null;
            try {
                const response = await this.$http.post(
                    `/lite-integrations/${this.selectedLiteIntegration.uuid}/reset-roblox-ids`
                );
                this.importSuccess =
                    response.data.message ??
                    'All Roblox IDs reset successfully.';
                this.experienceAssetsRevision++;
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to reset Roblox IDs.'
                );
            } finally {
                this.resettingRobloxIds = false;
            }
        },
        async applyAssetsToAllLocations() {
            if (
                !this.selectedLiteIntegration ||
                this.applyingAssetsToLocations
            ) {
                return;
            }
            const confirmed = window.confirm(
                `Apply all experience assets to all asset locations for ` +
                `"${this.selectedLiteIntegration.name}"?\n\n` +
                'Existing assignments will be kept and missing assignments ' +
                'will be added.'
            );
            if (!confirmed) {
                return;
            }
            this.applyingAssetsToLocations = true;
            this.error = null;
            this.importSuccess = null;
            try {
                const response = await this.$http.post(
                    `/lite-integrations/${this.selectedLiteIntegration.uuid}` +
                    '/apply-assets-to-all-locations'
                );
                const created = response.data.created ?? 0;
                this.importSuccess =
                    `${response.data.message} ${created} new ` +
                    `${created === 1 ? 'assignment' : 'assignments'} created.`;
                this.experienceAssetsRevision++;
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to apply assets to all locations.'
                );
            } finally {
                this.applyingAssetsToLocations = false;
            }
        },
        refreshSelectedLiteIntegration() {
            if (!this.selectedLiteIntegration) {
                return;
            }

            this.selectedLiteIntegration = this.liteIntegrations.find(
                ({ uuid }) => uuid === this.selectedLiteIntegration.uuid
            ) ?? null;
        },

        async incrementVersion(liteIntegration) {
            if (this.incrementingVersionUuid) {
                return;
            }

            const currentVersion = Number(liteIntegration.version_number ?? 1);
            const confirmed = window.confirm(
                `Increment version for "${liteIntegration.name}" from ` +
                `${currentVersion} to ${currentVersion + 1}?`
            );

            if (!confirmed) {
                return;
            }

            this.incrementingVersionUuid = liteIntegration.uuid;
            this.error = null;
            this.importSuccess = null;

            try {
                await this.$http.post(
                    `/lite-integrations/${liteIntegration.uuid}/increment-version`
                );

                await this.loadLiteIntegrations(this.pagination.currentPage);
                this.refreshSelectedLiteIntegration();
                this.importSuccess = `Version incremented for "${liteIntegration.name}".`;
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to increment the Lite Integration version.'
                );
            } finally {
                this.incrementingVersionUuid = null;
            }
        },

        handleLiteIntegrationUpdated(updatedLiteIntegration) {
            const index = this.liteIntegrations.findIndex(
                ({ uuid }) => uuid === updatedLiteIntegration.uuid
            );
            if (index !== -1) {
                this.$set(
                    this.liteIntegrations,
                    index,
                    updatedLiteIntegration
                );
            }
            this.selectedLiteIntegration = updatedLiteIntegration;
        },
    },
};
</script>